import { useState, useEffect, useCallback, useMemo } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import {
  TrendingUp, ArrowRight, Filter, AlertCircle, RefreshCw,
  CheckCircle2, CreditCard, Eye, UserPlus, Search, ChevronLeft, ChevronRight,
  MessageCircle, History, Clock, X, AlertTriangle, Loader2, Trash2
} from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";

export default function FunilConversao() {
  const [period, setPeriod] = useState("30d");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [selectedPlan, setSelectedPlan] = useState("all");
  const [selectedStage, setSelectedStage] = useState("all");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  // Estado do Modal de Timeline
  const [timelineUser, setTimelineUser] = useState(null);
  const [timelineEvents, setTimelineEvents] = useState([]);
  const [loadingTimeline, setLoadingTimeline] = useState(false);

  // Estado do Modal de Confirmação de Exclusão
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        period,
        plan: selectedPlan,
        stage: selectedStage,
        search: search.trim() || undefined,
        page,
        limit: 15,
      };
      if (period === "custom") {
        if (startDate) params.start_date = startDate;
        if (endDate) params.end_date = endDate;
      }
      const res = await api.get("/admin/funnel", { params });
      setData(res.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erro ao carregar métricas do funil");
    } finally {
      setLoading(false);
    }
  }, [period, startDate, endDate, selectedPlan, selectedStage, search, page]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handlePeriodChange = (newPeriod) => {
    setPeriod(newPeriod);
    setPage(1);
  };

  const handlePlanChange = (newPlan) => {
    setSelectedPlan(newPlan);
    setPage(1);
  };

  const handleStageChange = (newStage) => {
    setSelectedStage(newStage);
    setPage(1);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setPage(1);
    fetchData();
  };

  // Abrir Modal de Timeline
  const openTimeline = async (user) => {
    setTimelineUser(user);
    setLoadingTimeline(true);
    setTimelineEvents([]);
    try {
      const res = await api.get(`/admin/funnel/user/${user.user_id}/timeline`);
      setTimelineEvents(res.data?.events || []);
    } catch (err) {
      toast.error("Erro ao carregar histórico de eventos do utilizador.");
    } finally {
      setLoadingTimeline(false);
    }
  };

  // Executar exclusão dos registros do usuário
  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/admin/funnel/user/${deleteTarget.user_id}`);
      toast.success(`Histórico do utilizador ${deleteTarget.name || deleteTarget.email} removido com sucesso.`);
      
      // Atualização imediata do estado local sem necessidade de recarregar a página inteira
      setData((prev) => {
        if (!prev) return prev;
        const newUsers = prev.users.filter((u) => u.user_id !== deleteTarget.user_id);
        return {
          ...prev,
          users: newUsers,
          pagination: {
            ...prev.pagination,
            total: Math.max(0, (prev.pagination?.total || 1) - 1),
          },
        };
      });

      // Se o modal de timeline estiver aberto para este usuário, fechar também
      if (timelineUser?.user_id === deleteTarget.user_id) {
        setTimelineUser(null);
      }
      
      setDeleteTarget(null);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erro ao apagar registos do utilizador.");
    } finally {
      setDeleting(false);
    }
  };

  // Formatação de data/hora legível: DD/MM HH:mm
  const formatEventTime = (isoString) => {
    if (!isoString) return "";
    try {
      const d = new Date(isoString);
      const day = String(d.getDate()).padStart(2, "0");
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const hours = String(d.getHours()).padStart(2, "0");
      const minutes = String(d.getMinutes()).padStart(2, "0");
      return `${day}/${month} ${hours}:${minutes}`;
    } catch {
      return isoString;
    }
  };

  const renderStageBadge = (user) => {
    const stageKey = user.last_event;
    let badge = null;

    switch (stageKey) {
      case "user_registered":
        badge = (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium bg-blue-500/15 text-blue-300 border border-blue-500/30">
            <UserPlus className="w-3 h-3 text-blue-400" /> Cadastro
          </span>
        );
        break;
      case "plan_page_viewed":
        badge = (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium bg-slate-700/60 text-slate-300 border border-slate-600/50">
            <Eye className="w-3 h-3 text-slate-400" /> Planos Visualizados
          </span>
        );
        break;
      case "plan_selected":
        badge = (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium bg-amber-500/15 text-amber-300 border border-amber-500/30">
            <TrendingUp className="w-3 h-3 text-amber-400" /> Plano Selecionado
          </span>
        );
        break;
      case "checkout_redirected":
        badge = (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium bg-purple-500/15 text-purple-300 border border-purple-500/30">
            <CreditCard className="w-3 h-3 text-purple-400" /> Redirecionado ao Checkout
          </span>
        );
        break;
      case "payment_completed":
        badge = (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">
            <CheckCircle2 className="w-3 h-3 text-emerald-400" /> Pagamento Concluído
          </span>
        );
        break;
      default:
        badge = (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium bg-slate-800 text-slate-300 border border-slate-700">
            {stageKey}
          </span>
        );
    }

    let isAbandoned = false;
    if (stageKey === "checkout_redirected" && user.last_action_at) {
      const diffMs = Date.now() - new Date(user.last_action_at).getTime();
      const diffMinutes = diffMs / (1000 * 60);
      if (diffMinutes > 30) {
        isAbandoned = true;
      }
    }

    return (
      <div className="flex flex-col sm:flex-row sm:items-center gap-1.5 flex-wrap">
        {badge}
        {isAbandoned && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-semibold bg-rose-500/20 text-rose-300 border border-rose-500/40 animate-pulse">
            <AlertTriangle className="w-3 h-3 text-rose-400 shrink-0" />
            Abandono (&gt;30m)
          </span>
        )}
      </div>
    );
  };

  const getWhatsAppLink = (user) => {
    const telDigits = (user.telefone_e164 || user.telefone || "").replace(/\D/g, "");
    if (!telDigits) return null;
    const waNumber = telDigits.startsWith("55") ? telDigits : `55${telDigits}`;

    const nomeFormatado = (user.name || "").trim().split(" ")[0] || "Cliente";
    const planoFormatado = user.plan_name || (user.plan_id ? `MaturaMax ${user.plan_id.toUpperCase().replace("M", "")}` : "da MaturaMax");

    const mensagem = `Olá ${nomeFormatado}, tudo bem? Vi que tentou assinar o plano ${planoFormatado} na MaturaMax. Teve alguma dificuldade com o pagamento ou prefere pagar via PIX direto por aqui?`;

    return `https://wa.me/${waNumber}?text=${encodeURIComponent(mensagem)}`;
  };

  const metrics = data?.funnel_metrics || [];
  const rawUsers = data?.users || [];
  const pagination = data?.pagination || { total: 0, page: 1, pages: 1 };

  // Redundância de segurança no Frontend: ordenação decrescente por data da última ação / criação
  const users = useMemo(() => {
    const list = [...rawUsers];
    return list.sort((a, b) => {
      const timeA = new Date(a.last_action_at || a.created_at || 0).getTime();
      const timeB = new Date(b.last_action_at || b.created_at || 0).getTime();
      return timeB - timeA;
    });
  }, [rawUsers]);

  return (
    <div className="space-y-8" data-testid="funil-conversao-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-lg bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-emerald-400" />
            </div>
            <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-100">
              Funil de Conversão
            </h1>
          </div>
          <p className="text-sm text-slate-400 mt-1">
            Métricas internas de aquisição, navegação, intenção de compra e faturamento via InfinitePay.
          </p>
        </div>

        <button
          onClick={fetchData}
          disabled={loading}
          className="self-start sm:self-auto inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-medium border border-slate-700 transition-colors"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
          Atualizar Dados
        </button>
      </div>

      {/* Barra de Filtros */}
      <div className="bg-[#111827] border border-slate-800 rounded-xl p-4 sm:p-5 space-y-4">
        <div className="flex flex-wrap items-center gap-3">
          {/* Período */}
          <div className="flex items-center gap-1.5 bg-slate-900 border border-slate-800 rounded-lg p-1 text-xs">
            <button
              onClick={() => handlePeriodChange("today")}
              className={`px-3 py-1.5 rounded-md font-medium transition-colors ${
                period === "today" ? "bg-emerald-500/20 text-emerald-400 font-semibold" : "text-slate-400 hover:text-slate-200"
              }`}
            >
              Hoje
            </button>
            <button
              onClick={() => handlePeriodChange("7d")}
              className={`px-3 py-1.5 rounded-md font-medium transition-colors ${
                period === "7d" ? "bg-emerald-500/20 text-emerald-400 font-semibold" : "text-slate-400 hover:text-slate-200"
              }`}
            >
              Últimos 7 dias
            </button>
            <button
              onClick={() => handlePeriodChange("30d")}
              className={`px-3 py-1.5 rounded-md font-medium transition-colors ${
                period === "30d" ? "bg-emerald-500/20 text-emerald-400 font-semibold" : "text-slate-400 hover:text-slate-200"
              }`}
            >
              Últimos 30 dias
            </button>
            <button
              onClick={() => handlePeriodChange("custom")}
              className={`px-3 py-1.5 rounded-md font-medium transition-colors ${
                period === "custom" ? "bg-emerald-500/20 text-emerald-400 font-semibold" : "text-slate-400 hover:text-slate-200"
              }`}
            >
              Personalizado
            </button>
          </div>

          {/* Filtro por Plano */}
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-400 font-medium">Plano:</span>
            <select
              value={selectedPlan}
              onChange={(e) => handlePlanChange(e.target.value)}
              className="bg-slate-900 border border-slate-800 rounded-lg px-3 py-1.5 text-xs text-slate-200 focus:outline-none focus:border-emerald-500/50"
            >
              <option value="all">Todos os Planos</option>
              <option value="m1">MaturaMax 1 (R$ 19,90)</option>
              <option value="m5">MaturaMax 5 (R$ 44,90)</option>
              <option value="m10">MaturaMax 10 (R$ 78,98)</option>
              <option value="m20">MaturaMax 20 (R$ 109,90)</option>
              <option value="m50">MaturaMax 50 (R$ 199,90)</option>
            </select>
          </div>

          {/* Filtro por Última Etapa */}
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-400 font-medium">Última Etapa:</span>
            <select
              value={selectedStage}
              onChange={(e) => handleStageChange(e.target.value)}
              className="bg-slate-900 border border-slate-800 rounded-lg px-3 py-1.5 text-xs text-slate-200 focus:outline-none focus:border-emerald-500/50"
            >
              <option value="all">Todas as Etapas</option>
              <option value="user_registered">Cadastro</option>
              <option value="plan_page_viewed">Planos Visualizados</option>
              <option value="plan_selected">Plano Selecionado</option>
              <option value="checkout_redirected">Redirecionado ao Checkout</option>
              <option value="payment_completed">Pagamento Concluído</option>
            </select>
          </div>
        </div>

        {/* Datas personalizadas se ativo */}
        {period === "custom" && (
          <div className="flex flex-wrap items-center gap-3 pt-2 border-t border-slate-800/60">
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-400">De:</span>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="bg-slate-900 border border-slate-800 rounded-lg px-3 py-1.5 text-xs text-slate-200 focus:outline-none focus:border-emerald-500/50"
              />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-400">Até:</span>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="bg-slate-900 border border-slate-800 rounded-lg px-3 py-1.5 text-xs text-slate-200 focus:outline-none focus:border-emerald-500/50"
              />
            </div>
          </div>
        )}
      </div>

      {/* Cards do Funil Visual */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
        {metrics.map((m, idx) => {
          return (
            <div
              key={m.stage_key}
              className="relative bg-[#161F32] border border-slate-800 rounded-xl p-4 flex flex-col justify-between"
            >
              <div>
                <div className="flex items-center justify-between text-xs text-slate-400 mb-2">
                  <span className="font-semibold uppercase tracking-wider text-[11px] text-slate-300">
                    {idx + 1}. {m.label}
                  </span>
                  {idx > 0 && (
                    <span
                      className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${
                        m.drop_rate > 50
                          ? "bg-red-500/10 text-red-400"
                          : "bg-amber-500/10 text-amber-300"
                      }`}
                      title="Queda em relação à etapa anterior"
                    >
                      -{m.drop_rate}% queda
                    </span>
                  )}
                </div>
                <div className="text-2xl font-bold font-mono text-slate-100">
                  {m.count}
                </div>
              </div>

              <div className="mt-4 pt-3 border-t border-slate-800/80 flex items-center justify-between text-xs">
                <span className="text-slate-400">Taxa de avanço:</span>
                <span className="font-mono font-semibold text-emerald-400">
                  {idx === 0 ? "100%" : `${m.conversion_rate}%`}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Nota técnica sobre InfinitePay Hosted Checkout */}
      {data?.infinitepay_notice && (
        <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-4 flex items-start gap-3 text-xs text-slate-400">
          <AlertCircle className="w-4 h-4 text-slate-400 shrink-0 mt-0.5" />
          <p className="leading-relaxed">
            <strong className="text-slate-300">Transparência Técnica:</strong>{" "}
            {data.infinitepay_notice}
          </p>
        </div>
      )}

      {/* Tabela de Usuários por Última Etapa */}
      <div className="bg-[#111827] border border-slate-800 rounded-xl overflow-hidden">
        {/* Cabeçalho da Tabela com Busca */}
        <div className="p-4 sm:p-5 border-b border-slate-800 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h2 className="font-heading font-semibold text-base text-slate-100">
              Usuários por Última Etapa Alcançada
            </h2>
            <p className="text-xs text-slate-400 mt-0.5">
              Clique em qualquer linha ou nos botões de ação para abrir o histórico, acionar no WhatsApp ou remover o registro.
            </p>
          </div>

          <form onSubmit={handleSearchSubmit} className="flex items-center gap-2 max-w-sm w-full">
            <div className="relative flex-1">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
              <input
                type="text"
                placeholder="Buscar por nome, e-mail ou WhatsApp..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-9 pr-3 py-1.5 text-xs bg-slate-900 border border-slate-800 rounded-lg text-slate-200 placeholder:text-slate-500 focus:outline-none focus:border-emerald-500/50"
              />
            </div>
            <button
              type="submit"
              className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-medium rounded-lg border border-slate-700 transition-colors"
            >
              Buscar
            </button>
          </form>
        </div>

        {/* Listagem */}
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="bg-slate-900/60 text-slate-400 text-xs font-semibold uppercase tracking-wider border-b border-slate-800">
              <tr>
                <th className="px-4 py-3">Usuário</th>
                <th className="px-4 py-3">WhatsApp</th>
                <th className="px-4 py-3">Última Etapa</th>
                <th className="px-4 py-3">Plano / Valor</th>
                <th className="px-4 py-3">Última Ação</th>
                <th className="px-4 py-3 text-right">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/60">
              {users.length === 0 ? (
                <tr>
                  <td colSpan="6" className="text-center py-8 text-slate-400 text-xs">
                    {loading ? "Carregando usuários..." : "Nenhum evento/usuário encontrado com os filtros selecionados."}
                  </td>
                </tr>
              ) : (
                users.map((u) => {
                  const waLink = getWhatsAppLink(u);

                  return (
                    <tr
                      key={u.user_id}
                      onClick={() => openTimeline(u)}
                      className="hover:bg-slate-800/40 transition-colors cursor-pointer group"
                    >
                      {/* Usuário */}
                      <td className="px-4 py-3.5">
                        <div className="font-medium text-slate-200 group-hover:text-emerald-400 transition-colors">
                          {u.name || "N/A"}
                        </div>
                        <div className="text-xs text-slate-400">{u.email || "—"}</div>
                      </td>

                      {/* WhatsApp */}
                      <td className="px-4 py-3.5 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                        {u.telefone ? (
                          <div className="flex items-center gap-1.5 font-mono text-xs text-slate-300">
                            <span>{u.telefone}</span>
                          </div>
                        ) : (
                          <span className="text-xs text-slate-500">—</span>
                        )}
                      </td>

                      {/* Última Etapa */}
                      <td className="px-4 py-3.5 whitespace-nowrap">
                        {renderStageBadge(u)}
                      </td>

                      {/* Plano / Valor */}
                      <td className="px-4 py-3.5 whitespace-nowrap">
                        <div className="text-xs font-medium text-slate-300">
                          {u.plan_name || (u.plan_id ? `Plano ${u.plan_id.toUpperCase()}` : "—")}
                        </div>
                        {u.amount != null && (
                          <div className="text-[11px] font-mono text-emerald-400">
                            R$ {Number(u.amount).toFixed(2).replace(".", ",")}
                          </div>
                        )}
                      </td>

                      {/* Data / Hora */}
                      <td className="px-4 py-3.5 whitespace-nowrap text-xs text-slate-400">
                        {u.last_action_at ? new Date(u.last_action_at).toLocaleString("pt-BR") : "—"}
                      </td>

                      {/* Ações */}
                      <td className="px-4 py-3.5 whitespace-nowrap text-right" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-end gap-1.5">
                          {/* Histórico */}
                          <button
                            onClick={() => openTimeline(u)}
                            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 border border-slate-700 text-xs font-medium transition-colors"
                            title="Ver histórico cronológico do lead"
                          >
                            <History className="w-3.5 h-3.5 text-slate-400" />
                            Histórico
                          </button>

                          {/* WhatsApp */}
                          {waLink ? (
                            <a
                              href={waLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-300 border border-emerald-500/30 text-xs font-medium transition-colors"
                              title="Enviar mensagem contextualizada no WhatsApp"
                            >
                              <MessageCircle className="w-3.5 h-3.5" />
                              WhatsApp
                            </a>
                          ) : (
                            <span className="text-xs text-slate-600 px-1">Sem tel</span>
                          )}

                          {/* Botão de Excluir */}
                          <button
                            onClick={() => setDeleteTarget(u)}
                            className="p-1.5 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-400 hover:text-red-300 border border-red-500/20 hover:border-red-500/40 transition-colors"
                            title="Apagar registos deste utilizador do funil"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Paginação */}
        {pagination.pages > 1 && (
          <div className="p-4 border-t border-slate-800 flex items-center justify-between text-xs text-slate-400">
            <div>
              Total de leads: <strong className="text-slate-200">{pagination.total}</strong>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="p-1.5 rounded bg-slate-800 hover:bg-slate-700 disabled:opacity-40 text-slate-300 border border-slate-700 transition-colors"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span>
                Página <strong className="text-slate-200">{pagination.page}</strong> de{" "}
                <strong className="text-slate-200">{pagination.pages}</strong>
              </span>
              <button
                onClick={() => setPage((p) => Math.min(pagination.pages, p + 1))}
                disabled={page >= pagination.pages}
                className="p-1.5 rounded bg-slate-800 hover:bg-slate-700 disabled:opacity-40 text-slate-300 border border-slate-700 transition-colors"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* 1. MODAL DE TIMELINE INDIVIDUAL DO UTILIZADOR */}
      <Dialog open={!!timelineUser} onOpenChange={(v) => !v && setTimelineUser(null)}>
        <DialogContent className="bg-[#111827] border-slate-800 text-slate-100 sm:max-w-lg p-0 overflow-hidden">
          <div className="p-5 border-b border-slate-800">
            <div className="flex items-center justify-between">
              <div>
                <DialogTitle className="font-heading text-lg font-bold text-slate-100 flex items-center gap-2">
                  <History className="w-5 h-5 text-emerald-400" />
                  Histórico do Utilizador
                </DialogTitle>
                <p className="text-xs text-slate-400 mt-1">
                  Cronologia de interações de <strong className="text-slate-200">{timelineUser?.name}</strong> ({timelineUser?.email})
                </p>
              </div>
            </div>
          </div>

          <div className="p-5 max-h-[60vh] overflow-y-auto space-y-4">
            {loadingTimeline ? (
              <div className="py-12 flex flex-col items-center justify-center gap-3 text-slate-400 text-xs">
                <Loader2 className="w-6 h-6 animate-spin text-emerald-400" />
                Carregando cronologia de eventos...
              </div>
            ) : timelineEvents.length === 0 ? (
              <div className="py-8 text-center text-slate-500 text-xs">
                Nenhum evento registrado para este utilizador.
              </div>
            ) : (
              <div className="relative pl-6 space-y-6 before:absolute before:left-2.5 before:top-2 before:bottom-2 before:w-0.5 before:bg-slate-800">
                {timelineEvents.map((ev, index) => {
                  let dotColor = "bg-slate-500";
                  if (ev.event_name === "user_registered") dotColor = "bg-blue-400 ring-4 ring-blue-500/20";
                  else if (ev.event_name === "plan_page_viewed") dotColor = "bg-slate-400 ring-4 ring-slate-500/20";
                  else if (ev.event_name === "plan_selected") dotColor = "bg-amber-400 ring-4 ring-amber-500/20";
                  else if (ev.event_name === "checkout_redirected") dotColor = "bg-purple-400 ring-4 ring-purple-500/20";
                  else if (ev.event_name === "payment_completed") dotColor = "bg-emerald-400 ring-4 ring-emerald-500/20";

                  return (
                    <div key={ev.id || index} className="relative">
                      {/* Ponto na timeline */}
                      <div className={`absolute -left-6 top-1 w-2.5 h-2.5 rounded-full ${dotColor}`} />

                      <div className="bg-slate-900/80 border border-slate-800/80 rounded-lg p-3 space-y-1">
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-mono text-[11px] font-semibold text-emerald-400 flex items-center gap-1">
                            <Clock className="w-3 h-3 text-emerald-500" />
                            {formatEventTime(ev.created_at)}
                          </span>
                          <span className="text-[10px] uppercase tracking-wider text-slate-500 font-mono">
                            {ev.event_name}
                          </span>
                        </div>
                        <p className="text-xs text-slate-200 font-medium leading-relaxed">
                          • {ev.label}
                        </p>
                        {ev.metadata && Object.keys(ev.metadata).length > 0 && (
                          <div className="text-[10px] text-slate-500 font-mono pt-1">
                            {ev.metadata.order_nsu && <span>NSU: {ev.metadata.order_nsu} </span>}
                            {ev.metadata.source && <span>Origem: {ev.metadata.source} </span>}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Footer do Modal de Timeline */}
          <div className="p-4 bg-slate-900/60 border-t border-slate-800 flex items-center justify-between">
            {timelineUser && getWhatsAppLink(timelineUser) ? (
              <a
                href={getWhatsAppLink(timelineUser)}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-emerald-950 font-semibold text-xs transition-colors"
              >
                <MessageCircle className="w-3.5 h-3.5" />
                Contactar no WhatsApp
              </a>
            ) : (
              <div />
            )}
            <button
              onClick={() => setTimelineUser(null)}
              className="px-3 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs font-medium border border-slate-700 transition-colors"
            >
              Fechar
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* 2. MODAL DE CONFIRMAÇÃO DE EXCLUSÃO */}
      <Dialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <DialogContent className="bg-[#111827] border-slate-800 text-slate-100 sm:max-w-md p-6">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg font-bold text-slate-100 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-400" />
              Remover do Histórico
            </DialogTitle>
          </DialogHeader>

          <div className="py-3 text-sm text-slate-300 space-y-2">
            <p>
              Tem a certeza que deseja remover este registo do histórico?
            </p>
            <div className="p-3 bg-slate-900/80 rounded-lg border border-slate-800 text-xs text-slate-400 space-y-1">
              <div><strong>Utilizador:</strong> {deleteTarget?.name}</div>
              <div><strong>E-mail:</strong> {deleteTarget?.email}</div>
              <div><strong>Última Etapa:</strong> {deleteTarget?.last_event_label}</div>
            </div>
            <p className="text-xs text-slate-500">
              Esta ação removerá todos os eventos de conversão deste utilizador da base de dados do funil.
            </p>
          </div>

          <div className="flex items-center justify-end gap-3 pt-3 border-t border-slate-800">
            <button
              onClick={() => setDeleteTarget(null)}
              disabled={deleting}
              className="px-4 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs font-medium border border-slate-700 transition-colors disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              onClick={handleDeleteConfirm}
              disabled={deleting}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white text-xs font-semibold transition-colors disabled:opacity-50 shadow-sm"
            >
              {deleting ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  Removendo...
                </>
              ) : (
                <>
                  <Trash2 className="w-3.5 h-3.5" />
                  Confirmar Remoção
                </>
              )}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
